import React, { useState, useRef } from "react";
import { styled } from "@mui/material/styles";
import Avatar from "@mui/material/Avatar";
import AvatarEditor from "react-avatar-editor";
import AddAPhotoIcon from "@mui/icons-material/AddAPhoto";
import Slider from "@mui/material/Slider";
import Typography from "@mui/material/Typography";
import { ProcessImageUpload } from "../../scripts/SharedComponents/ProcessImageUpload";
import { GreenButton, WhiteButton } from "../../scripts/SharedComponents/RTKButtons";

const RTKAvatarEditor = ({ currentUserID, name }) => {
  const [image, setImage] = useState(null);
  const [scale, setScale] = useState(1);
  const [rotate, setRotate] = useState(0);
  const [position, setPosition] = useState({ x: 0.5, y: 0.5 });
  const [croppedImage, setCroppedImage] = useState(null);
  const editorRef = useRef(null);

  const VisuallyHiddenInput = styled("input")({
    clip: "rect(0 0 0 0)",
    clipPath: "inset(50%)",
    height: 1,
    overflow: "hidden",
    position: "absolute",
    bottom: 0,
    left: 0,
    whiteSpace: "nowrap",
    width: 1,
  });

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      setImage(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleScaleChange = (_, newValue) => {
    setScale(newValue);
  };

  const handleRotateChange = (_, newValue) => {
    setRotate(newValue);
  };

  const handlePositionChange = (pos) => {
    setPosition(pos);
  };

  const dataURLtoBlob = (dataURL) => {
    const arr = dataURL.split(",");
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) u8arr[n] = bstr.charCodeAt(n);
    return new Blob([u8arr], { type: mime });
  };

  const handleCropAndUpload = async () => {
    if (!editorRef.current) return;

    const canvas = editorRef.current.getImageScaledToCanvas();
    const dataURL = canvas.toDataURL("image/jpeg");
    const blob = dataURLtoBlob(dataURL);

    setCroppedImage(dataURL);

    try {
      const processedImage = await ProcessImageUpload(blob, {
        type: "avatar",
        userId: currentUserID,
      });
      console.log("✅ Avatar uploaded:", processedImage);
    } catch (err) {
      console.error("❌ Upload failed:", err);
    }
  };

  return (
    <div>
      <div className="mt-2 mb-2">
        <GreenButton component="label" variant="contained" startIcon={<AddAPhotoIcon />} fullWidth size="large" className="py-3" sx={{ borderRadius: "0.75rem", marginBottom: "1rem" }}>
          Upload Profile Image
          <VisuallyHiddenInput type="file" accept="image/*" onChange={handleFileChange} />
        </GreenButton>
      </div>

      {image && (
        <>
          <Typography className="pb-3 text-lg py-6">Profile Image Editor:</Typography>
          <div className="py-2 pb-4">Use the editor below to pan, zoom and rotate your selected image to suit.</div>
          <AvatarEditor className="rounded-lg" ref={editorRef} image={image} width={300} height={300} border={30} borderRadius={150} scale={scale} rotate={rotate} position={position} onPositionChange={handlePositionChange} />

          <div className="bg-slate-100 border-[1px] border-slate-200 rounded-xl p-8 my-6">
            <div className="mb-6">
              <Typography gutterBottom>Image Zoom</Typography>
              <Slider value={scale} onChange={handleScaleChange} min={1} max={5} step={0.01} aria-label="Image Zoom" />
            </div>

            <div className="mb-6">
              <Typography gutterBottom>Image Rotation</Typography>
              <Slider value={rotate} onChange={handleRotateChange} min={0} max={360} step={1} aria-label="Image Rotation" />
            </div>

            <div className="flex justify-end">
              <WhiteButton onClick={handleCropAndUpload} variant="contained" size="large">
                Save Avatar
              </WhiteButton>
            </div>
            
          </div>

        </>
      )}

      {croppedImage && (
        <>
          <Typography className="pb-3 text-lg">Profile Image Preview:</Typography>
          <div className="flex flex-col justify-center gap-3 bg-slate-100 border-[1px] border-slate-200 rounded-xl p-8 mb-6">
            <Avatar src={croppedImage} sx={{ width: 140, height: 140 }} />
            <div className="text-lg">{name}</div>
          </div>
        </>
      )}
    </div>
  );
};

export default RTKAvatarEditor;
